import React, { useEffect, useMemo, useState } from "react";
import type { Budget, Car, Client, Service } from "../../../types";
import { budgetsApi, clientsApi } from "../../../services";
import { Modal, FormActions } from "../../shared";
import { Select } from "../../shared/Select";
import { Input } from "../../shared/Input";
import { Icons } from "../../shared/Icons";
import {
  applyDiscountPercent,
  computeBudgetTotalFromLines,
  computeDiscountPercent,
  formatBudgetCurrency,
  getBudgetServices,
  getOptionalSubServiceRelations,
  getRequiredSubServiceRelations,
  getServiceSuggestedPrice,
  normalizeBudgetSubServices,
} from "../../../utils/budgetUtils";
import { formatServiceSelectLabel } from "../../../utils/serviceEffortPrice";

export interface BudgetLineForm {
  key: string;
  serviceId: number;
  serviceName: string;
  suggestedPrice: number;
  price: number;
  discountPercent: number;
  optionalSubServiceIds: number[];
}

interface CreateBudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clients: Client[];
  services: Service[];
  editingBudget?: Budget | null;
  /** Client already chosen in the budgets list filter. */
  initialClientId?: string;
}

function createEmptyLine(): BudgetLineForm {
  return {
    key: `line-${Date.now()}-${Math.random()}`,
    serviceId: 0,
    serviceName: "",
    suggestedPrice: 0,
    price: 0,
    discountPercent: 0,
    optionalSubServiceIds: [],
  };
}

function linesFromBudget(
  budget: Budget,
  activeServices: Service[]
): BudgetLineForm[] {
  const services = getBudgetServices(budget);
  return services.map((line, index) => {
    const service =
      activeServices.find((s) => s.id === line.serviceId) ?? line.service;
    const subs = normalizeBudgetSubServices(line);
    const optionalSubServiceIds = subs
      .filter((sub) => !sub.isRequired)
      .map((sub) => sub.subServiceId);
    const suggested = service
      ? getServiceSuggestedPrice(
          service as Service,
          budget.clientCar?.effort,
          optionalSubServiceIds
        )
      : parseFloat(String(line.price));
    const price = parseFloat(String(line.price));
    return {
      key: `edit-${line.serviceId}-${index}`,
      serviceId: line.serviceId,
      serviceName:
        (service as Service)?.name ?? `Serviço #${line.serviceId}`,
      suggestedPrice: suggested,
      price,
      discountPercent: computeDiscountPercent(suggested, price),
      optionalSubServiceIds,
    };
  });
}

export const CreateBudgetModal: React.FC<CreateBudgetModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clients,
  services,
  editingBudget,
  initialClientId,
}) => {
  const isEditing = Boolean(editingBudget);
  const activeServices = useMemo(
    () => services.filter((s) => s.isActive !== false),
    [services]
  );

  const [clientId, setClientId] = useState<string>("");
  const [clientCarId, setClientCarId] = useState<string>("");
  const [clientCars, setClientCars] = useState<Car[]>([]);
  const [lines, setLines] = useState<BudgetLineForm[]>([createEmptyLine()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountInputs, setDiscountInputs] = useState<Record<string, string>>({});
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [addingSubForLine, setAddingSubForLine] = useState<string | null>(null);

  const selectedCar = clientCars.find((c) => String(c.id) === clientCarId);

  useEffect(() => {
    if (!isOpen) return;

    if (editingBudget) {
      setClientId(String(editingBudget.clientId));
      setClientCarId(
        editingBudget.clientCarId ? String(editingBudget.clientCarId) : ""
      );
      setLines(
        getBudgetServices(editingBudget).length > 0
          ? linesFromBudget(editingBudget, activeServices)
          : [createEmptyLine()]
      );
    } else {
      setClientId(initialClientId ?? "");
      setClientCarId("");
      setLines([createEmptyLine()]);
    }
    setDiscountInputs({});
    setPriceInputs({});
    setAddingSubForLine(null);
    setError(null);
  }, [isOpen, editingBudget, activeServices, initialClientId]);

  useEffect(() => {
    if (!clientId) {
      setClientCars([]);
      if (!isEditing) setClientCarId("");
      return;
    }

    let cancelled = false;
    clientsApi.getCarsByClient(Number(clientId)).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setClientCars(res.data);
      } else {
        setClientCars([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [clientId, isEditing]);

  useEffect(() => {
    if (!selectedCar) return;
    setLines((prev) =>
      prev.map((line) => {
        if (line.serviceId <= 0) return line;
        const service = activeServices.find((s) => s.id === line.serviceId);
        if (!service) return line;
        const suggested = getServiceSuggestedPrice(
          service,
          selectedCar.effort,
          line.optionalSubServiceIds
        );
        const price = applyDiscountPercent(suggested, line.discountPercent);
        return { ...line, suggestedPrice: suggested, price };
      })
    );
  }, [selectedCar, activeServices]);

  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const carOptions = [
    { value: "", label: "Nenhum veículo" },
    ...clientCars.map((car) => ({
      value: String(car.id),
      label: `${car.model} — ${car.plate}`,
    })),
  ];

  const usedServiceIds = new Set(
    lines.filter((l) => l.serviceId > 0).map((l) => l.serviceId)
  );

  const total = computeBudgetTotalFromLines(lines);

  const updateLine = (key: string, patch: Partial<BudgetLineForm>) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
  };

  const handleServiceChange = (key: string, serviceId: number) => {
    const service = activeServices.find((s) => s.id === serviceId);
    if (!service) return;
    const suggested = getServiceSuggestedPrice(
      service,
      selectedCar?.effort,
      []
    );
    updateLine(key, {
      serviceId,
      serviceName: service.name,
      suggestedPrice: suggested,
      price: suggested,
      discountPercent: 0,
      optionalSubServiceIds: [],
    });
    setAddingSubForLine(null);
    setDiscountInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPriceInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddOptionalSub = (
    key: string,
    service: Service,
    subServiceId: number
  ) => {
    const line = lines.find((l) => l.key === key);
    if (!line || line.optionalSubServiceIds.includes(subServiceId)) return;

    const optionalSubServiceIds = [...line.optionalSubServiceIds, subServiceId];
    const suggested = getServiceSuggestedPrice(
      service,
      selectedCar?.effort,
      optionalSubServiceIds
    );
    const price = applyDiscountPercent(suggested, line.discountPercent);
    updateLine(key, { optionalSubServiceIds, suggestedPrice: suggested, price });
    setAddingSubForLine(null);
  };

  const handleRemoveOptionalSub = (
    key: string,
    service: Service,
    subServiceId: number
  ) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;

    const optionalSubServiceIds = line.optionalSubServiceIds.filter(
      (id) => id !== subServiceId
    );
    const suggested = getServiceSuggestedPrice(
      service,
      selectedCar?.effort,
      optionalSubServiceIds
    );
    const price = applyDiscountPercent(suggested, line.discountPercent);
    updateLine(key, { optionalSubServiceIds, suggestedPrice: suggested, price });
  };

  const handleDiscountChange = (key: string, discountPercent: number) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    const price = applyDiscountPercent(line.suggestedPrice, discountPercent);
    updateLine(key, { discountPercent, price });
  };

  const handleDiscountInputChange = (key: string, raw: string) => {
    setDiscountInputs((prev) => ({ ...prev, [key]: raw }));

    if (raw === "" || raw === ".") return;

    const discountPercent = parseFloat(raw);
    if (!Number.isNaN(discountPercent)) {
      handleDiscountChange(key, discountPercent);
    }
  };

  const handleDiscountInputBlur = (key: string) => {
    const raw = discountInputs[key];
    if (raw === "" || raw === ".") {
      handleDiscountChange(key, 0);
    }
    setDiscountInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handlePriceChange = (key: string, price: number) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    const discountPercent = computeDiscountPercent(line.suggestedPrice, price);
    updateLine(key, { price, discountPercent });
  };

  const handlePriceInputChange = (key: string, raw: string) => {
    setPriceInputs((prev) => ({ ...prev, [key]: raw }));

    if (raw === "" || raw === ".") return;

    const price = parseFloat(raw);
    if (!Number.isNaN(price)) {
      handlePriceChange(key, price);
    }
  };

  const handlePriceInputBlur = (key: string) => {
    const raw = priceInputs[key];
    if (raw === "" || raw === ".") {
      handlePriceChange(key, 0);
    }
    setPriceInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const addLine = () => {
    setLines((prev) => [...prev, createEmptyLine()]);
  };

  const removeLine = (key: string) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.key !== key);
      return next.length > 0 ? next : [createEmptyLine()];
    });
    if (addingSubForLine === key) setAddingSubForLine(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!clientId) {
      setError("Selecione um cliente");
      return;
    }

    const validLines = lines.filter((l) => l.serviceId > 0);
    if (validLines.length === 0) {
      setError("Adicione pelo menos um serviço");
      return;
    }

    const payload = {
      clientId: Number(clientId),
      clientCarId: clientCarId ? Number(clientCarId) : null,
      services: validLines.map((l) => ({
        serviceId: l.serviceId,
        price: l.price,
        subServiceIds: l.optionalSubServiceIds,
      })),
    };

    setLoading(true);
    try {
      const response =
        isEditing && editingBudget
          ? await budgetsApi.updateBudget(editingBudget.id, {
              clientCarId: payload.clientCarId,
              services: payload.services,
            })
          : await budgetsApi.createBudget(payload);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.error || "Erro ao salvar orçamento");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao salvar orçamento"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Editar Orçamento" : "Novo Orçamento"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-300">
            {error}
          </div>
        )}

        <Select
          label="Cliente"
          value={clientId}
          onChange={(v) => {
            setClientId(String(v));
            if (!isEditing) setClientCarId("");
          }}
          options={clientOptions}
          placeholder="Selecione o cliente"
          disabled={isEditing}
          required
          searchable
        />

        {clientId && (
          <Select
            label="Veículo (opcional)"
            value={clientCarId}
            onChange={(v) => setClientCarId(String(v))}
            options={carOptions}
            placeholder="Selecione o veículo"
            searchable
          />
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Serviços</h3>
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1 text-sm font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
            >
              <Icons.Add sx={{ fontSize: 18 }} />
              Adicionar serviço
            </button>
          </div>

          {lines.map((line) => {
            const service = activeServices.find((s) => s.id === line.serviceId);
            const requiredSubs = service
              ? getRequiredSubServiceRelations(service)
              : [];
            const optionalRelations = service
              ? getOptionalSubServiceRelations(service)
              : [];
            const selectedOptional = optionalRelations.filter((rel) =>
              line.optionalSubServiceIds.includes(rel.subServiceId)
            );
            const availableOptional = optionalRelations.filter(
              (rel) => !line.optionalSubServiceIds.includes(rel.subServiceId)
            );

            const serviceOptions = activeServices
              .filter(
                (s) =>
                  s.id === line.serviceId || !usedServiceIds.has(s.id)
              )
              .map((s) => ({
                value: s.id,
                label: formatServiceSelectLabel(s),
              }));

            return (
              <div
                key={line.key}
                className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-900/50 p-3 sm:p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <Select
                      label="Serviço"
                      value={line.serviceId || ""}
                      onChange={(v) =>
                        handleServiceChange(line.key, Number(v))
                      }
                      options={serviceOptions}
                      placeholder="Selecione o serviço"
                      searchable
                    />
                  </div>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(line.key)}
                      className="mt-6 p-1.5 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                      title="Remover serviço"
                      aria-label="Remover serviço"
                    >
                      <Icons.Delete sx={{ fontSize: 20 }} />
                    </button>
                  )}
                </div>

                {line.serviceId > 0 && service && (
                  <>
                    {(requiredSubs.length > 0 ||
                      selectedOptional.length > 0) && (
                      <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-3 space-y-2">
                        <p className="text-xs font-medium text-gray-500 dark:text-slate-400">
                          Subserviços
                        </p>
                        <ul className="space-y-1.5">
                          {requiredSubs.map((rel) => (
                            <li
                              key={`req-${rel.id}`}
                              className="flex items-center justify-between text-sm text-gray-700 dark:text-slate-200"
                            >
                              <span>
                                {rel.subService.name}
                                <span className="ml-1 text-xs text-gray-400 dark:text-slate-500">
                                  (incluso)
                                </span>
                              </span>
                              <span className="text-gray-500 dark:text-slate-400">
                                {formatBudgetCurrency(rel.price)}
                              </span>
                            </li>
                          ))}
                          {selectedOptional.map((rel) => (
                            <li
                              key={`opt-${rel.id}`}
                              className="flex items-center justify-between text-sm text-gray-700 dark:text-slate-200 gap-2"
                            >
                              <span className="min-w-0 truncate">
                                {rel.subService.name}
                              </span>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-gray-500 dark:text-slate-400">
                                  {formatBudgetCurrency(rel.price)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveOptionalSub(
                                      line.key,
                                      service,
                                      rel.subServiceId
                                    )
                                  }
                                  className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded"
                                  title="Remover subserviço"
                                  aria-label="Remover subserviço"
                                >
                                  <Icons.Delete sx={{ fontSize: 16 }} />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>

                        {availableOptional.length > 0 && (
                          <div className="pt-1">
                            {addingSubForLine === line.key ? (
                              <div className="flex items-end gap-2">
                                <div className="flex-1 min-w-0">
                                  <Select
                                    label="Adicionar subserviço"
                                    value=""
                                    onChange={(v) => {
                                      if (v) {
                                        handleAddOptionalSub(
                                          line.key,
                                          service,
                                          Number(v)
                                        );
                                      }
                                    }}
                                    options={availableOptional.map((rel) => ({
                                      value: rel.subServiceId,
                                      label: `${rel.subService.name} — ${formatBudgetCurrency(rel.price)}`,
                                    }))}
                                    placeholder="Selecione"
                                    searchable
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setAddingSubForLine(null)}
                                  className="mb-1 px-2 py-1 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setAddingSubForLine(line.key)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                              >
                                <Icons.Add sx={{ fontSize: 16 }} />
                                Adicionar subserviço opcional
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {requiredSubs.length === 0 &&
                      optionalRelations.length > 0 &&
                      selectedOptional.length === 0 && (
                        <div className="rounded-lg border border-dashed border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
                          {addingSubForLine === line.key ? (
                            <div className="flex items-end gap-2">
                              <div className="flex-1 min-w-0">
                                <Select
                                  label="Adicionar subserviço opcional"
                                  value=""
                                  onChange={(v) => {
                                    if (v) {
                                      handleAddOptionalSub(
                                        line.key,
                                        service,
                                        Number(v)
                                      );
                                    }
                                  }}
                                  options={availableOptional.map((rel) => ({
                                    value: rel.subServiceId,
                                    label: `${rel.subService.name} — ${formatBudgetCurrency(rel.price)}`,
                                  }))}
                                  placeholder="Selecione"
                                  searchable
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => setAddingSubForLine(null)}
                                className="mb-1 px-2 py-1 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setAddingSubForLine(line.key)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                            >
                              <Icons.Add sx={{ fontSize: 16 }} />
                              Adicionar subserviço opcional
                            </button>
                          )}
                        </div>
                      )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                          Preço sugerido
                        </label>
                        <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 py-2">
                          {formatBudgetCurrency(line.suggestedPrice)}
                        </p>
                      </div>
                      <Input
                        label="Desconto (%)"
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={
                          line.key in discountInputs
                            ? discountInputs[line.key]
                            : String(line.discountPercent)
                        }
                        onChange={(e) =>
                          handleDiscountInputChange(line.key, e.target.value)
                        }
                        onBlur={() => handleDiscountInputBlur(line.key)}
                      />
                      <Input
                        label="Preço final (R$)"
                        type="number"
                        min={0}
                        step={0.01}
                        value={
                          line.key in priceInputs
                            ? priceInputs[line.key]
                            : String(line.price)
                        }
                        onChange={(e) =>
                          handlePriceInputChange(line.key, e.target.value)
                        }
                        onBlur={() => handlePriceInputBlur(line.key)}
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between rounded-xl bg-sky-50 dark:bg-sky-950/50 border border-sky-100 dark:border-sky-800 px-4 py-3">
          <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Total</span>
          <span className="text-lg font-bold text-sky-800 dark:text-sky-300">
            {formatBudgetCurrency(total)}
          </span>
        </div>

        <FormActions
          onCancel={onClose}
          onSubmit={() => {}}
          loading={loading}
          submitText={isEditing ? "Salvar" : "Criar orçamento"}
          submitType="submit"
          isEditing={isEditing}
        />
      </form>
    </Modal>
  );
};
